import { SelectItem } from '@wui/common-library'
import { isBotPServed } from '@wui/im'
import debounce from 'lodash.debounce'
import { storeToRefs } from 'pinia'
import { ref, watch, computed } from 'vue'

import { useStoreRooms } from '@/entities/room'

import { useApi } from '@/shared/composables/api'
import { usePaginatedData } from '@/shared/composables/use-paginated-data'
import { Contact, GetContactsQuery, Response, WithPagination } from '@/shared/types/api'
import { isBotCatalogSearchQuery, matchesPublicBotSearch, resolveBotContact, searchBotsForQuery } from '@/shared/utils/contacts'
import sort from '@/shared/utils/sort'

import { useStoreContacts } from '..'

const isDigitsOnlySearch = (value: string): boolean => /^\d+$/.test(value)

const isRequestCancelled = (e: unknown): boolean => {
  const err = e as { code?: string }
  return err.code === 'ERR_CANCELED'
}

/** Поля для participants-select вместе с serverSideFilter и этим composable */
export const CONTACT_PARTICIPANTS_SELECT_SEARCH_FIELDS: (keyof Contact)[] = [
  'name',
  'internalNumber',
  'mobilePhone',
]

export const useSearchContactsByStringPaginated = () => {
  const { fetchNext: fetchNextContacts, setContactIfDoesNotExists, findById } = useStoreContacts()
  const storeRooms = useStoreRooms()
  const { contactsItemsSortedByUsing } = storeToRefs(storeRooms)

  const string = ref('')
  const botItems = ref<Contact[]>([])
  let botSearchGeneration = 0

  const collectExistingBotContacts = (query: string): Contact[] => {
    const result: Contact[] = []
    storeRooms.rooms.forEach((room) => {
      if (!isBotPServed(room.pServedUser)) {
        return
      }

      if (!matchesPublicBotSearch(query, {
        name: room.name,
        publicIdentity: '',
        description: '',
        pServed: room.pServedUser,
      })) {
        return
      }

      const fromRoom = [...room.subscribers.values()].find(contact => contact.imLogin === room.pServedUser)
      result.push(fromRoom ?? resolveBotContact(room.pServedUser, room.name))
    })
    return result
  }

  const mapContactsToSelectItems = (contacts: Contact[]): SelectItem<Contact>[] => {
    return [...contacts]
      .map(contact => findById(contact.id) ?? contact)
      .sort(sort.contacts.byFavoriteThenName)
      .map(contact => ({ label: contact.name, value: contact }))
  }

  const foundItems = computed<SelectItem<Contact>[]>(() => {
    if (isBotCatalogSearchQuery(string.value)) {
      return mapContactsToSelectItems(botItems.value)
    }

    return mapContactsToSelectItems(list.value)
  })

  const items = computed(() => string.value ? foundItems.value : contactsItemsSortedByUsing.value)

  const filters = computed<Partial<GetContactsQuery>>(() => {
    const result: Partial<GetContactsQuery> = {}
    const searchVal = string.value

    if (searchVal && !isDigitsOnlySearch(searchVal)) {
      result.Filters = [{
        AvailableOperations: ['contains'],
        AvailableValues: searchVal,
        PropertyName: 'names',
        TypeFilter: 'string',
      }]
    }

    return result
  })

  let controller: AbortController | undefined
  const { fetchNext: fetchNextSearchedContacts, reset, list } = usePaginatedData<Contact>({
    size: 50,
    idKey: 'id',
    fetchData: async (data: GetContactsQuery) => {
      if (isBotCatalogSearchQuery(string.value)) {
        return
      }

      if (controller) {
        controller.abort()
      }

      controller = new AbortController()

      const searchVal = string.value
      const isDigitsOnly = Boolean(searchVal && isDigitsOnlySearch(searchVal))

      /** Как в contacts-filterable / fetchContactsByNameOrNumber: OR в одном запросе на бэкенде не работает */
      if (isDigitsOnly) {
        if (data.Page && data.Page > 1) {
          return
        }

        const needle = searchVal as string
        const base: GetContactsQuery = {
          Page: 1,
          Size: data.Size ?? 50,
          SortBy: data.SortBy ?? 'name',
        }

        const internalFilters: NonNullable<GetContactsQuery['Filters']> = [{
          AvailableOperations: ['contains'],
          AvailableValues: needle,
          PropertyName: 'internalNumber',
          TypeFilter: 'phone',
        }]

        const mobileFilters: NonNullable<GetContactsQuery['Filters']> = [{
          AvailableOperations: ['contains'],
          AvailableValues: needle,
          PropertyName: 'mobilePhone',
          TypeFilter: 'phone',
        }]

        try {
          const api = useApi()
          const [internalRes, mobileRes] = await Promise.all([
            api.getContacts({ ...base, Filters: internalFilters }, controller),
            api.getContacts({ ...base, Filters: mobileFilters }, controller),
          ])

          const seen = new Map<string, Contact>()
          for (const contact of [...internalRes?.data?.list ?? [], ...mobileRes?.data?.list ?? []]) {
            seen.set(contact.id, contact)
          }

          const mergedList = [...seen.values()]
          mergedList.forEach(setContactIfDoesNotExists)

          const total = mergedList.length

          return {
            data: {
              list: mergedList,
              page: {
                total: total > 0 ? total : 1,
                lastPage: 1,
                currentPage: 1,
                perPage: mergedList.length || data.Size || 50,
              },
            },
          } as Response<WithPagination<Contact[]>>
        } catch (e) {
          if (!isRequestCancelled(e)) {
            console.error(e)
          }
          return
        }
      }

      return useApi().getContacts({
        ...data,
        ...filters.value,
      }, controller)
        .then(response => {
          const { data: { list: contactsList }} = response
          contactsList.forEach(setContactIfDoesNotExists)

          return response
        })
        .catch(e => {
          if (!isRequestCancelled(e)) {
            console.error(e)
          }
        })
    },
  })

  const fetchNext = async () => {
    try {
      if (isBotCatalogSearchQuery(string.value)) {
        return
      }
      if (string.value) {
        await fetchNextSearchedContacts()
      } else {
        await fetchNextContacts()
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error(e)
      }
    }
  }

  watch(string, (v) => {
    if (!isBotCatalogSearchQuery(v)) {
      return
    }

    botItems.value = []
    reset()
  })

  watch(string, debounce(async (v: string) => {
    const generation = ++botSearchGeneration
    reset()

    if (!isBotCatalogSearchQuery(v)) {
      botItems.value = []
    }

    if (!v) {
      return
    }

    if (isBotCatalogSearchQuery(v)) {
      try {
        const existingBots = collectExistingBotContacts(v)
        const bots = await searchBotsForQuery(v)
        if (generation !== botSearchGeneration) {
          return
        }

        const merged: Contact[] = []
        const seen = new Set<string>()
        ;[...existingBots, ...bots].forEach((contact) => {
          const key = contact.imLogin.toLowerCase()
          if (seen.has(key)) {
            return
          }
          seen.add(key)
          merged.push(contact)
        })

        merged.forEach(setContactIfDoesNotExists)
        botItems.value = merged
      } catch (e) {
        if (generation !== botSearchGeneration) {
          return
        }
        console.error(e)
        botItems.value = []
      }
      return
    }

    fetchNextSearchedContacts()
  }, 500))

  return {
    string,
    items,
    allItems: contactsItemsSortedByUsing,
    fetchNext,
  }
}
